import { bbox } from 'ol/loadingstrategy';
import { Circle, Stroke, Fill, Style, Text } from 'ol/style';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import 'whatwg-fetch';
import Collection from 'ol/Collection';
import Point from 'ol/geom/Point.js';
import TileLayer from 'ol/layer/Tile';
import ImageLayer from 'ol/layer/Image';
import WMTS from 'ol/source/WMTS';
import Datafordeler from '@/api/Datafordeler';
import TileWMS from 'ol/source/TileWMS';
import ImageWMS from 'ol/source/ImageWMS';
import { isLayerVisible } from './util';
import { color } from '@/util/theme';

import { tileGrid } from './Projection';
import GeoDanmark from '../api/GeoDanmark';

const kftoken = 'b6fd85faf914982afe0ac3239017266c';
export const wmts = new TileLayer({
  title: 'Skaermkort',
  visible: 'false',
  opacity: 0.6,
  extent: [120000, 5904000, 958860.8, 6499000],
  source: new WMTS({
    url: 'https://{a-m}.services.kortforsyningen.dk/topo_skaermkort_daempet?token=' + kftoken,
    format: 'image/jpeg',
    matrixSet: 'View1',
    layer: 'dtk_skaermkort_daempet',
    style: 'default',
    tileGrid: tileGrid,
  }),
});
export const ortho = new TileLayer({
  title: 'Ortho',
  opacity: 0.4,
  visible: false,
  extent: [120000, 5904000, 958860.8, 6499000],
  source: new WMTS({
    url: 'https://{a-m}.services.kortforsyningen.dk/orto_foraar?token=' + kftoken,
    format: 'image/jpeg',
    matrixSet: 'View1',
    layer: 'orto_foraar',
    style: 'default',
    tileGrid: tileGrid,
  }),
});
export const forvaltning = new TileLayer({
  title: 'Forvaltning',
  opacity: 0.4,
  visible: false,
  extent: [120000, 5904000, 958860.8, 6499000],
  source: new TileWMS({
    url: 'https://{a-m}.services.kortforsyningen.dk/forvaltning?token=' + kftoken,
    params: { 'LAYERS': ['basis_kort'], 'TRANSPARENT': 'FALSE' },
  }),
});
export const matrikel = new TileLayer({
  title: 'Matrikel',
  opacity: 0.4,
  maxResolution: 1.5,
  visible: true,
  source: new TileWMS({
    url: 'https://{a-m}.services.kortforsyningen.dk/mat?token=' + kftoken,
    params: { 'LAYERS': ['MatrikelSkel', 'OptagetVej'], 'STYLES': ['Sorte_skel', 'default'], 'TRANSPARENT': 'TRUE' },
  }),
});
export const husnr = new ImageLayer({
  title: 'Husnummer',
  opacity: 0.5,
  visible: true,
  source: new ImageWMS({
    url: 'https://services.kortforsyningen.dk/forvaltning?token=' + kftoken,
    params: { 'LAYERS': ['husnummer_basis_kort'], 'TRANSPARENT': 'TRUE' },
  }),
});
function formatVejkode (vejkode) {
  return vejkode != null ? vejkode : 'Ingen';
}

export const geodkWFS = new VectorLayer({
  token: null,
  name: 'geodkWFS',
  maxResolution: 1.5,
  source: new VectorSource({
    loader: function (extent) {
      // Datafordeler.getVejmidteBrudt(extent)
      GeoDanmark.getGeoDkObjektByBboxPaged(extent, geodkWFS.token)
        .then(features => {
          geodkWFS.getSource().addFeatures(features);
        });
    },
    strategy: bbox,
    projection: 'EPSG:25832',
  }),
  style: function (feature, resolution) {
    const vejkode = formatVejkode(feature.get('vejkode'));
    const isSti = formatVejkode(feature.get('vejmidtetype') === 'Sti'); // Sti
    var styleGeodk = [
      new Style({
        stroke: new Stroke({
          color: 'rgba(255, 255, 255, 0.01)',
          width: 10,
        }),
      }),
      new Style({
        stroke: new Stroke({
          color: color.default_color,
          width: resolution > 1 ? 1 : isSti ? 2 : 3,
          lineDash: isSti ? [15, 5] : undefined,
        }),
        text: getTextStyle(color.default_color),
      }),
    ];
    styleGeodk[1].getText().setText('(' + vejkode + ')');

    return styleGeodk;
  },
});

export function createAdressepunkterHighlight (map) {
  return new VectorLayer({
    map,
    name: 'adressepunkterHighlight',
    source: new VectorSource(),
    style: function (feature, resolution) {
      const isVisible = isLayerVisible(map, 'Husnummer');
      if (!isVisible) {
        return null;
      }
      const retning360 = (feature.get('tekstretning') + 200) / 400 * 360;
      const retning = retning360 * Math.PI / 180;
      const style = new Style({
        image: new Circle({
          radius: resolution < 0.5 ? 16 : resolution < 1 ? 12 : 8,
          fill: new Fill({
            color: color.default_color_background,
          }),
          stroke: new Stroke({
            color: color.default_color,
          }),
        }),
        text: new Text({
          text: feature.get('husnr'),
          rotation: retning,
          font: 'bold 12px sans-serif',
          fill: new Fill({ color: color.default_color }),
          stroke: new Stroke({ color: 'white', width: 1 }),
          padding: [1, 1, 1, 1],
        }),
        stroke: new Stroke({
          color: 'rgb(165, 0, 255)',
          width: 2,
        }),
      });
      return style;
    },
  });
}

function getTextStyle (color) {
  return new Text({
    font: 'bold 16px sans-serif',
    placement: 'line',
    fill: new Fill({ color }),
    stroke: new Stroke({ color: 'white', width: 3 }),
    padding: [1, 1, 1, 1],
  });
}
export function createGeodkWFSHighlight (map) {
  return new VectorLayer({
    map: map,
    source: new VectorSource(),
    style: function (feature, resolution) {
      const isSti = formatVejkode(feature.get('vejmidtetype') === 'Sti'); // Sti
      let styles = [
        new Style({
          stroke: new Stroke({
            color: color.default_color_dark,
            width: resolution > 1 ? 1 : isSti ? 4 : 7,
            lineDash: isSti ? [15, 5] : undefined,
            lineCap: 'butt',
          }),
        }),
      ];
      function getStyleForSegment (startOrEnd) {
        return new Style({
          geometry: new Point(startOrEnd),
          image: new Circle({
            radius: resolution > 1 ? 3 : 7,
            fill: new Fill({
              color: color.default_color_dark,
            }),
          }),
        });
      }
      var geometry = feature.getGeometry();
      const coordinates = geometry.getCoordinates();
      styles.push(getStyleForSegment(coordinates[0]));
      styles.push(getStyleForSegment(coordinates[coordinates.length - 1]));
      return styles;
    },
  });
}

export const vejmidteWMS = new TileLayer({
  title: 'Fot2007Vejmidte',
  maxResolution: 5,
  minResolution: 1.5,
  opacity: 0.5,
  visible: true,
  source: new TileWMS({
    url: 'https://{a-m}.services.kortforsyningen.dk/fot2007_nohistory?token=' + kftoken,
    params: {
      'LAYERS': ['VEJMIDTE'],
      'STYLES': ['default'],
      'TRANSPARENT': 'TRUE',
      'SLD_BODY':
        `<StyledLayerDescriptor version="1.0.0" xmlns="http://www.opengis.net/sld" xmlns:ogc="http://www.opengis.net/ogc">
          <NamedLayer>
            <Name>VEJMIDTE</Name>
            <UserStyle>
              <FeatureTypeStyle>
                <Rule>
                  <LineSymbolizer>
                    <Stroke>
                      <CssParameter name="stroke">${color.default_color_light}</CssParameter>
                      <CssParameter name="stroke-width">1</CssParameter>
                    </Stroke>
                  </LineSymbolizer>
                </Rule>
              </FeatureTypeStyle>
            </UserStyle>
          </NamedLayer>
        </StyledLayerDescriptor>`,
    },
  }),
});

export const kommuneWMS = new TileLayer({
  title: 'DagiKommune',
  // maxResolution: 200,
  minResolution: 1.5,
  opacity: 0.5,
  visible: true,
  source: new TileWMS({
    url: 'https://{a-m}.services.kortforsyningen.dk/dagi?token=' + kftoken,
    params: {
      'LAYERS': ['kommune'],
      'STYLES': ['default'],
      'TRANSPARENT': 'TRUE',
      'SLD_BODY':
        `<StyledLayerDescriptor version="1.0.0" xmlns="http://www.opengis.net/sld" xmlns:ogc="http://www.opengis.net/ogc">
          <NamedLayer>
            <Name>kommune</Name>
            <UserStyle>
              <Name>kommune</Name>
              <FeatureTypeStyle>
                <Rule>
                  <LineSymbolizer>
                    <Stroke>
                      <CssParameter name="stroke">${color.default_color_light}</CssParameter>
                      <CssParameter name="stroke-width">3</CssParameter>
                    </Stroke>
                  </LineSymbolizer>
                </Rule>
              </FeatureTypeStyle>
            </UserStyle>
          </NamedLayer>
        </StyledLayerDescriptor>`,
    },
  }),
});

/*
Use this syntax for specific kommune. PropertyName for kommunekode is unknown. Ask SDFE.
                  <Filter>
                    <PropertyIsEqualTo>
                      <PropertyName>navn</PropertyName>
                      <Literal>Herning</Literal>
                    </PropertyIsEqualTo>
                  </Filter>

*/

export var geodkSelectedFeatures = new Collection();
var vectorSource = new VectorSource({
  features: geodkSelectedFeatures,
});
export var geodkVectorSelected = new VectorLayer({
  name: 'geodkVectorSelected',
  source: vectorSource,
  style: (feature, resolution) => {
    var vejkode = formatVejkode(feature.get('vejkode'));
    const isSti = formatVejkode(feature.get('vejmidtetype') === 'Sti'); // Sti
    var styles = [
      new Style({
        stroke: new Stroke({
          color: color.selected_color,
          width: resolution > 1 ? 1 : isSti ? 4 : 7,
          lineDash: isSti ? [15, 5] : undefined,
          lineCap: 'butt',
        }),
        text: getTextStyle(color.selected_color_dark),
      }),
    ];
    styles[0].getText().setText('(' + vejkode + ')');
    var geometry = feature.getGeometry();
    function getStyleForSegment (startOrEnd) {
      return new Style({
        geometry: new Point(startOrEnd),
        image: new Circle({
          radius: resolution > 1 ? 3 : 7,
          fill: new Fill({
            color: color.selected_color,
          }),
        }),
      });
    }
    const coordinates = geometry.getCoordinates();
    styles.push(getStyleForSegment(coordinates[0]));
    styles.push(getStyleForSegment(coordinates[coordinates.length - 1]));
    return styles;
  },
});
export function creategeodkVectorSelectedHighlight (map) {
  return new VectorLayer({
    name: 'geodkVectorSelectedHighlight',
    map: map,
    source: new VectorSource(),
    style: (feature, resolution) => {
      var vejkode = formatVejkode(feature.get('vejkode'));
      const isSti = formatVejkode(feature.get('vejmidtetype') === 'Sti'); // Sti
      var styles = [
        new Style({
          stroke: new Stroke({
            color: color.selected_highlight_color,
            width: resolution > 1 ? 1 : isSti ? 4 : 7,
            lineDash: isSti ? [15, 5] : undefined,
            lineCap: 'butt',
          }),
          text: getTextStyle(color.selected_highlight_color),
        }),
      ];
      styles[0].getText().setText('(' + vejkode + ')');
      var geometry = feature.getGeometry();
      function getStyleForSegment (startOrEnd) {
        return new Style({
          geometry: new Point(startOrEnd),
          image: new Circle({
            radius: resolution > 1 ? 3 : 7,
            fill: new Fill({
              color: color.selected_highlight_color,
            }),
          }),
        });
      }
      const coordinates = geometry.getCoordinates();
      styles.push(getStyleForSegment(coordinates[0]));
      styles.push(getStyleForSegment(coordinates[coordinates.length - 1]));
      return styles;
    },
  });
}
